'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useMarketingUrls } from '@/features/marketing/hooks/use-marketing-urls';

import {
  checkImportStatus,
  createImportRequest,
  ensureFormatCheckerSession,
  extractResumeText,
  uploadResumeFile,
} from '../api/format-check';
import { getTidbits } from '../lib/tidbits';
import type { FormatCheckerVariant } from '../types';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ALLOWED_EXTENSIONS = ['.pdf', '.docx'];

const POLL_INTERVAL = 3000;
const MAX_POLL_ATTEMPTS = 60;

export type FormatCheckState =
  | 'idle'
  | 'loading'
  | 'error';

export type UseFormatCheckOptions = {
  variant: FormatCheckerVariant;
  targetFormat: string;
};

export function useFormatCheck({
  variant,
  targetFormat,
}: UseFormatCheckOptions) {
  const { webDomain } = useMarketingUrls();
  const [state, setState] = useState<FormatCheckState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [resumeText, setResumeText] = useState<string>('');
  const [loaderStep, setLoaderStep] = useState<number>(0);
  const [tidbit, setTidbit] = useState<string>('');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tidbitRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loaderStepRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (tidbitRef.current) clearInterval(tidbitRef.current);
      if (loaderStepRef.current) clearInterval(loaderStepRef.current);
    };
  }, []);

  const reset = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (tidbitRef.current) {
      clearInterval(tidbitRef.current);
      tidbitRef.current = null;
    }
    if (loaderStepRef.current) {
      clearInterval(loaderStepRef.current);
      loaderStepRef.current = null;
    }
    setState('idle');
    setErrorMessage('');
    setLoaderStep(0);
    setTidbit('');
  }, []);

  const isAllowedFile = useCallback((file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large. Maximum 10MB allowed.');
      return false;
    }
    if (ALLOWED_TYPES.includes(file.type)) {
      return true;
    }
    const fileName = file.name.toLowerCase();
    const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) =>
      fileName.endsWith(ext),
    );
    if (!hasValidExtension) {
      toast.error('Invalid file type. Only PDF and DOCX files are allowed.');
      return false;
    }
    return true;
  }, []);

  const handleFileSelect = useCallback(
    async (file: File | null) => {
      if (!file) {
        setSelectedFile(null);
        return;
      }
      if (!isAllowedFile(file)) {
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      try {
        const text = await extractResumeText(file);
        setResumeText(text);
      } catch {
        // Non-blocking: text extraction is a convenience fallback.
      }
    },
    [isAllowedFile],
  );

  const startTidbits = useCallback(() => {
    const tidbits = getTidbits(variant);
    if (tidbits.length === 0) return;

    let index = Math.floor(Math.random() * tidbits.length);
    setTidbit(tidbits[index] || '');

    tidbitRef.current = setInterval(() => {
      index = (index + 1) % tidbits.length;
      setTidbit(tidbits[index] || '');
    }, 5000);
  }, [variant]);

  const startLoaderSteps = useCallback(() => {
    // Cycle through steps 0-2 during import; step 3 is shown on completion.
    let step = 0;
    setLoaderStep(step);
    loaderStepRef.current = setInterval(() => {
      step = (step + 1) % 3;
      setLoaderStep(step);
    }, 7500);
  }, []);

  const redirectToEditor = useCallback(
    (resumeId: number) => {
      const formatParam =
        targetFormat === 'universal' ? '' : `&format=${encodeURIComponent(targetFormat)}`;
      const url =
        `${webDomain}/account/portfoliosettings?mode=editor` +
        `&auto_format_check=true&resumeId=${encodeURIComponent(String(resumeId))}${formatParam}`;
      window.location.href = url;
    },
    [targetFormat, webDomain],
  );

  const pollImportStatus = useCallback(
    (requestId: number) => {
      let attempts = 0;
      setLoaderStep(2);
      if (loaderStepRef.current) {
        clearInterval(loaderStepRef.current);
        loaderStepRef.current = null;
      }

      pollingRef.current = setInterval(async () => {
        attempts += 1;
        if (attempts > MAX_POLL_ATTEMPTS) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setState('error');
          setErrorMessage(
            'Import is taking longer than expected. Please try again.',
          );
          return;
        }

        try {
          const statusData = await checkImportStatus(requestId);

          if (statusData.status === 'ERROR') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setState('error');
            setErrorMessage(
              statusData.message || 'Import failed. Please try again.',
            );
            return;
          }

          if (
            statusData.request_status === 'FAILED' ||
            statusData.request_status === 'REJECTED'
          ) {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setState('error');
            setErrorMessage('Import failed. Please try again.');
            return;
          }

          if (
            statusData.status === 'SUCCESS' &&
            statusData.request_status === 'COMPLETED'
          ) {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            const resumeId =
              statusData.resume_id ||
              statusData.data?.resume_id ||
              statusData.import_status?.resume_id;
            if (!resumeId) {
              setState('error');
              setErrorMessage(
                'Import completed but resume ID was not returned.',
              );
              return;
            }
            setLoaderStep(3);
            setTimeout(() => {
              redirectToEditor(resumeId);
            }, 1500);
          }
        } catch (error) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setState('error');
          setErrorMessage(
            error instanceof Error
              ? error.message
              : 'Failed to check import status.',
          );
        }
      }, POLL_INTERVAL);
    },
    [redirectToEditor],
  );

  const submit = useCallback(async () => {
    if (!selectedFile && !resumeText.trim()) {
      toast.error('Please upload a resume file or paste your resume contents.');
      return;
    }

    reset();
    setState('loading');
    startLoaderSteps();
    startTidbits();

    try {
      await ensureFormatCheckerSession();

      let requestId: number;
      if (selectedFile) {
        const fileId = await uploadResumeFile(selectedFile);
        requestId = await createImportRequest({
          file_id: fileId,
          type: 'import_only',
          target_format: targetFormat,
        });
      } else {
        requestId = await createImportRequest({
          resume_text: resumeText.trim(),
          file_id: 0,
          type: 'import_only',
          check_content_length: false,
          target_format: targetFormat,
        });
      }

      pollImportStatus(requestId);
    } catch (error) {
      if (tidbitRef.current) {
        clearInterval(tidbitRef.current);
        tidbitRef.current = null;
      }
      if (loaderStepRef.current) {
        clearInterval(loaderStepRef.current);
        loaderStepRef.current = null;
      }
      setState('error');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to start import. Please try again.',
      );
    }
  }, [
    selectedFile,
    resumeText,
    targetFormat,
    reset,
    startLoaderSteps,
    startTidbits,
    pollImportStatus,
  ]);

  return {
    state,
    errorMessage,
    selectedFile,
    resumeText,
    loaderStep,
    tidbit,
    isAllowedFile,
    handleFileSelect,
    setResumeText,
    submit,
    reset,
  };
}
